<script setup>
import { computed, ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
  subFilterValue: {
    type: String,
    default: '',
  },
  subFilterOptions: {
    type: Array,
    default: () => [],
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['chatTabChange', 'subFilterChange']);
const openFilterMenuTab = ref('');

const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

const onTabChange = selectedItem => {
  if (selectedItem.key !== props.activeTab) {
    openFilterMenuTab.value = '';
    emit('chatTabChange', selectedItem.key);
  }
};

const isFilterTab = tabKey => {
  return [
    wootConstants.ASSIGNEE_TYPE.ME,
    wootConstants.ASSIGNEE_TYPE.ALL,
  ].includes(tabKey);
};

const onTabMainClick = item => {
  const isActiveFilterTab =
    item.key === props.activeTab && isFilterTab(item.key);
  if (isActiveFilterTab) {
    openFilterMenuTab.value =
      openFilterMenuTab.value === item.key ? '' : item.key;
    return;
  }
  onTabChange(item);
};

const closeFilterMenu = () => {
  openFilterMenuTab.value = '';
};

const onSelectSubFilter = value => {
  emit('subFilterChange', value);
  openFilterMenuTab.value = '';
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      if (!props.items.length) return;
      if (props.activeTab === wootConstants.ASSIGNEE_TYPE.ALL) {
        onTabChange(props.items[0]);
        return;
      }
      const nextIndex = (activeTabIndex.value + 1) % props.items.length;
      onTabChange(props.items[nextIndex]);
    },
  },
};

const getDisplayCount = count => {
  const normalized = Number(count) || 0;
  return normalized > 99 ? '99+' : normalized;
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <woot-tabs :index="activeTabIndex" class="w-full px-3 -mt-1 py-0 [&_ul]:p-0">
    <li
      v-for="item in items"
      :key="item.key"
      class="relative flex items-center gap-1 flex-shrink-0 my-0 mx-2 ltr:first:ml-0 rtl:first:mr-0 ltr:last:mr-0 rtl:last:ml-0"
      :class="[
        item.key === activeTab
          ? 'border-b border-n-brand text-n-blue-text'
          : 'border-b border-transparent text-n-slate-11',
      ]"
    >
      <button
        type="button"
        class="flex items-center h-9 py-2 text-sm font-medium transition-colors hover:text-n-slate-12"
        :class="{ 'gap-1': isFilterTab(item.key) }"
        @click="onTabMainClick(item)"
      >
        <span class="inline-flex items-center">
          {{ item.name }}
          <span
            v-if="isFilterTab(item.key)"
            class="i-lucide-chevron-down size-3 ltr:ml-0.5 rtl:mr-0.5"
            :class="[
              openFilterMenuTab === item.key ? 'rotate-180' : '',
              item.key === activeTab ? 'opacity-100' : 'opacity-40',
            ]"
          />
        </span>
        <span
          class="rounded-md h-5 w-8 flex items-center justify-center text-xxs font-semibold tabular-nums my-0 mx-1 px-0 py-0"
          :class="[
            item.key === activeTab
              ? 'bg-n-brand/10 dark:bg-n-brand/20 text-n-blue-text'
              : 'bg-n-alpha-black2 dark:bg-n-solid-3 text-n-slate-11',
          ]"
        >
          {{ getDisplayCount(item.count) }}
        </span>
      </button>

      <div
        v-if="
          item.key === activeTab &&
          isFilterTab(item.key) &&
          openFilterMenuTab === item.key
        "
        v-on-clickaway="closeFilterMenu"
        class="absolute top-full mt-1 z-40 min-w-[120px] p-1 rounded-lg border border-n-weak dark:border-n-strong/50 bg-n-alpha-3 backdrop-blur-[100px] shadow-lg"
        :class="[
          isOnExpandedLayout
            ? 'ltr:right-0 rtl:left-0'
            : 'ltr:left-0 rtl:right-0',
        ]"
        @click.stop
      >
        <button
          v-for="option in subFilterOptions"
          :key="option.value"
          type="button"
          class="w-full h-8 px-2 rounded-md text-sm flex items-center justify-between text-n-slate-12 hover:bg-n-alpha-2"
          :class="{ '!bg-n-alpha-2': option.value === subFilterValue }"
          @click="onSelectSubFilter(option.value)"
        >
          <span>{{ option.label }}</span>
          <span
            v-if="option.value === subFilterValue"
            class="i-lucide-check size-3 text-n-blue-text"
          />
        </button>
      </div>
    </li>
  </woot-tabs>
</template>
